export const CONSENT_COOKIE_NAME = "cedrum_consent";
export const CONSENT_STORAGE_KEY = "cedrum_consent";
export const CONSENT_VERSION = "2026-02-21";
const COOKIE_TTL_DAYS = 365;
const LEGACY_CONSENT_STORAGE_KEYS = Object.freeze(["cedrum_consent_v1"]);

const listeners = new Set();

const defaultConsent = Object.freeze({
  necessary: true,
  diagnostics: false,
  marketing: false,
  version: CONSENT_VERSION,
  updatedAt: null,
  hasChoice: false,
});

function parseBoolean(value, fallback = false) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }

  return fallback;
}

function sanitizeConsent(value) {
  const safe = value && typeof value === "object" ? value : {};
  const rawVersion =
    typeof safe.version === "string" && safe.version.trim()
      ? safe.version.trim()
      : null;
  const isLegacyConsent =
    rawVersion === "v1" ||
    Object.hasOwn(safe, "analytics") ||
    Object.hasOwn(safe, "pending");

  return {
    necessary: true,
    diagnostics: parseBoolean(safe.diagnostics ?? safe.analytics, false),
    marketing: parseBoolean(safe.marketing, false),
    version: isLegacyConsent ? CONSENT_VERSION : rawVersion ?? CONSENT_VERSION,
    updatedAt:
      typeof safe.updatedAt === "string" && safe.updatedAt.trim()
        ? safe.updatedAt.trim()
        : null,
    hasChoice: parseBoolean(safe.hasChoice, false),
  };
}

function parseJson(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readCookie(name) {
  if (typeof document === "undefined") {
    return null;
  }

  const parts = document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean);

  const prefixed = `${name}=`;
  const chunk = parts.find((entry) => entry.startsWith(prefixed));
  if (!chunk) {
    return null;
  }

  try {
    return decodeURIComponent(chunk.slice(prefixed.length));
  } catch {
    return chunk.slice(prefixed.length);
  }
}

function writeCookie(name, value, days) {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Expires=${expires}; SameSite=Lax${secure}`;
}

function readStorageKey(key) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return parseJson(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

function clearLegacyStorage() {
  if (typeof window === "undefined") {
    return;
  }

  for (const legacyKey of LEGACY_CONSENT_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(legacyKey);
    } catch {
      // Ignore storage cleanup failures
    }
  }
}

function writeStorage(consent) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Ignore storage write failures
  }

  clearLegacyStorage();
}

function notify(consent) {
  for (const listener of listeners) {
    try {
      listener(consent);
    } catch {
      // Ignore listener errors
    }
  }
}

export function getConsent() {
  const storage = sanitizeConsent(readStorageKey(CONSENT_STORAGE_KEY));
  if (storage.hasChoice) {
    return storage;
  }

  for (const legacyKey of LEGACY_CONSENT_STORAGE_KEYS) {
    const legacy = sanitizeConsent(readStorageKey(legacyKey));
    if (legacy.hasChoice) {
      writeCookie(CONSENT_COOKIE_NAME, JSON.stringify(legacy), COOKIE_TTL_DAYS);
      writeStorage(legacy);
      return legacy;
    }
  }

  const cookie = sanitizeConsent(parseJson(readCookie(CONSENT_COOKIE_NAME)));
  if (cookie.hasChoice) {
    writeStorage(cookie);
    return cookie;
  }

  return { ...defaultConsent };
}

export function isConsentCurrent(consent = getConsent()) {
  return Boolean(consent.hasChoice) && consent.version === CONSENT_VERSION;
}

export function setConsent(next) {
  const merged = sanitizeConsent({
    ...getConsent(),
    ...next,
    necessary: true,
    hasChoice: true,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  });

  writeCookie(CONSENT_COOKIE_NAME, JSON.stringify(merged), COOKIE_TTL_DAYS);
  writeStorage(merged);
  notify(merged);
  return merged;
}

export function hasConsent(category, consent = getConsent()) {
  const safe = sanitizeConsent(consent);
  const normalized = String(category || "").trim().toLowerCase();

  if (normalized === "necessary") {
    return true;
  }

  if (normalized === "diagnostics" || normalized === "analytics") {
    return Boolean(safe.diagnostics);
  }

  if (normalized === "marketing") {
    return Boolean(safe.marketing);
  }

  return false;
}

export function subscribeConsent(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }

  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function onConsentChange(listener) {
  return subscribeConsent(listener);
}

export function getDefaultConsent() {
  return { ...defaultConsent };
}
